import React from 'react';

import {
  Folder,
  Media,
  Contact,
  EditorProject,
  DataHook,
  FolderType,
  Product,
} from '../../../types';

import {useWindowDimensions} from 'react-native';

import MediaItem from './mediaitem';

import ContactItem from './contactitem';

import FolderItem from './folderitem';

import ProjectItem from './projectitem';

import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';

import {FileStackParams, FolderParams} from '../../../types/nav';
import {useAppDispatch} from '../../../store/hooks';
import {setFolderMenuItemId} from '../../../store/slices/ui';
import {useFolder} from '../../../hooks/useFolder';
import {ProductItem} from './productitem';
import {useFolderMutations} from '../../../hooks/useFolderMutations';

const enableMove = false;

export interface ContentItemProps {
  dataHook: DataHook;
  styles: any;
  i: Contact | Media | Folder;
  width: number;
  onDragEnd?: () => void;
  onDragStart?: () => void;
}

const ContentItem = (ContentItemProps: ContentItemProps) => {
  const dispatch = useAppDispatch();
  const {dataHook, styles, i, onDragEnd, onDragStart} =
    ContentItemProps;
  const navigation = useNavigation<NavigationProp<FileStackParams>>();
  const route = useRoute();
  const params = route.params as FolderParams;
  const currentFolderId = params?.folderId;
  const dim = useWindowDimensions();
  const drawerOffset = dim.width > 700 ? 80 : 0;
  const width = ContentItemProps.width - drawerOffset;
  const iconSize = 30;
  const iconColor = 'black';
  const showMenu = () =>
    dispatch(setFolderMenuItemId({itemId: i.id}));
  const hideMenu = () =>
    dispatch(setFolderMenuItemId({itemId: undefined}));
  if ((i as Product).isProduct) {
    const {
      onMediaPress,
      onRemoveMediaPress,
      onDownloadMediaPress,
      onCopyLinkPress,
      onEditMediaPress,
      isLoadingMedia,
      onRemoveFile,
      currentMediaId,
      onExportFile,
    } = dataHook as ReturnType<typeof useFolder>;
    return (
      <ProductItem
        key={i.id}
        i={i as Media & Product}
        width={width}
        onMediaPress={onMediaPress}
        onRemoveMediaPress={() => onRemoveMediaPress(i.id)}
        onDownloadMediaPress={onDownloadMediaPress}
        onCopyLinkPress={onCopyLinkPress}
        onEditMediaPress={onEditMediaPress}
        onDragEnd={onDragEnd!}
        onDragStart={onDragStart!}
        showMenu={showMenu}
        hideMenu={hideMenu}
        onExportFile={() => onExportFile(i.id)}
        onRemoveFile={() => onRemoveFile(i.id)}
        itemLoading={
          i.id === currentMediaId && isLoadingMedia
            ? currentMediaId
            : false
        }
        isPlaying={
          i.id === currentMediaId && !isLoadingMedia ? true : false
        }
        enableMove={enableMove}
      />
    );
  }
  if ((i as Media).isMediaNew) {
    const {
      onMediaPress,
      onRemoveMediaPress,
      onDownloadMediaPress,
      onCopyLinkPress,
      onEditMediaPress,
      isLoadingMedia,
      onRemoveFile,
      currentMediaId,
      onExportFile,
    } = dataHook as ReturnType<typeof useFolder>;
    return (
      <MediaItem
        key={i.id}
        i={i as Media}
        width={width}
        onMediaPress={onMediaPress}
        onRemoveMediaPress={() => onRemoveMediaPress(i.id)}
        onDownloadMediaPress={onDownloadMediaPress}
        onCopyLinkPress={onCopyLinkPress}
        onEditMediaPress={onEditMediaPress}
        onDragEnd={onDragEnd!}
        onDragStart={onDragStart!}
        showMenu={showMenu}
        hideMenu={hideMenu}
        onExportFile={() => onExportFile(i.id)}
        onRemoveFile={() => onRemoveFile(i.id)}
        itemLoading={
          i.id === currentMediaId && isLoadingMedia
            ? currentMediaId
            : false
        }
        isPlaying={
          i.id === currentMediaId && !isLoadingMedia ? true : false
        }
        enableMove={enableMove}
      />
    );
  }

  if (
    (i as Media).isEncoderProject ||
    (i as EditorProject).isEditorProject
  ) {
    return (
      <ProjectItem
        item={i as Media}
        width={width}
        folderType={dataHook.folderType as FolderType}
        onRemoveFolder={() => dataHook.onRemoveFolder(i as Folder)}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        showMenu={showMenu}
        hideMenu={hideMenu}
      />
    );
  }

  if ((i as Contact).isContact) {
    return <ContactItem key={i.id} i={i as Contact} width={width} />;
  }
  if ((i as Folder).isFolder) {
    const {
      onRemoveFolder,
      onFolderActionPress,
      abortEditFolder,
      onShareFolder,
    } = dataHook as ReturnType<typeof useFolderMutations>;
    return (
      <FolderItem
        key={i.id}
        folder={{
          ...(i as Folder),
          isOpen: i.id === currentFolderId,
        }}
        styles={styles}
        iconColor={iconColor}
        iconSize={iconSize}
        enableMove={enableMove}
        width={width}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        showMenu={showMenu}
        hideMenu={hideMenu}
        isOpen={i.id === currentFolderId}
        onRemoveFolder={() => onRemoveFolder(i as Folder)}
        onShareFolder={() => onShareFolder(i as Folder)}
        onFolderActionPress={() => onFolderActionPress(i.id)}
        onFolderPress={() =>
          navigation.navigate({
            name: 'Folder',
            params: {
              folderId: (i as Folder).id,
              title: (i as Folder).title,
              parentFolderId: i.folderId as string,
              mnemonic: (i as Folder).mnemonic,
              userId: (i as Folder).userId,
            },
          })
        }
        isSecondLevel={true}
        abortEditFolder={() => abortEditFolder(i.id)}>
        {i.id === currentFolderId ? <></> : null}
      </FolderItem>
    );
  }
  return <></>;
};

export default ContentItem;
